import { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TabContext, useTabManager } from '../contexts/TabContext';
import { Zap, BookOpen, Table, Dna, FileCode } from 'lucide-react';

export function useTabNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const tabContext = useContext(TabContext);
  const isInTabContext = !!tabContext;
  
  // Get project ID from the path
  const projectMatch = location.pathname.match(/^\/projects\/([^\/]+)/);
  const projectId = projectMatch?.[1];
  
  const openGenerationTab = (jobId: string, jobName?: string) => {
    if (!isInTabContext || !tabContext || !projectId) return;
    
    // Check if a tab for this job already exists
    const existingTab = tabContext.tabs.find(
      tab => tab.type === 'generation' && tab.data?.jobId === jobId
    );
    
    if (existingTab) {
      // Tab already exists, just switch to it
      tabContext.switchTab(existingTab.id);
      navigate(`/projects/${projectId}/jobs/${jobId}`);
      return;
    }
    
    // Create the tab
    tabContext.openTab({
      type: 'generation',
      title: 'Loading...', // Temporary title while loading
      icon: Zap,
      closable: true,
      projectId,
      data: { jobId },
      loading: true // Always set loading to true so TabContext formats the title correctly
    });
    
    // Navigate after creating the tab
    navigate(`/projects/${projectId}/jobs/${jobId}`);
  };
  
  const openEntryTab = (entryId: string, entryName: string) => {
    if (!isInTabContext || !tabContext || !projectId) return;
    
    // Check if a tab for this entry already exists
    const existingTab = tabContext.tabs.find(
      tab => tab.type === 'entry' && tab.data?.entryId === entryId
    );
    
    if (existingTab) {
      // Tab already exists, just switch to it
      tabContext.switchTab(existingTab.id);
      navigate(`/projects/${projectId}/entries/${entryId}`);
      return;
    }
    
    // Create the tab
    tabContext.openTab({
      type: 'entry',
      title: entryName,
      icon: BookOpen,
      closable: true,
      projectId,
      data: { entryId },
      loading: false // Set loading to false since we already have the name
    });
    
    // Navigate after creating the tab
    navigate(`/projects/${projectId}/entries/${entryId}`);
  };
  
  const openTableTab = (dataEntryId: string, tableName: string) => {
    if (!isInTabContext || !tabContext || !projectId) return;
    
    // Check if a tab for this table already exists
    const existingTab = tabContext.tabs.find(
      tab => tab.type === 'table' && tab.data?.dataEntryId === dataEntryId
    );
    
    if (existingTab) {
      // Tab already exists, just switch to it
      tabContext.switchTab(existingTab.id);
      navigate(`/projects/${projectId}/data-entries/${dataEntryId}`);
      return;
    }
    
    // Create the tab
    tabContext.openTab({
      type: 'table',
      title: tableName,
      icon: Table,
      closable: true,
      projectId,
      data: { dataEntryId },
      loading: false // Set loading to false since we already have the name
    });
    
    // Navigate after creating the tab
    navigate(`/projects/${projectId}/data-entries/${dataEntryId}`);
  };
  
  const openOdysseyTab = (odysseyJobId: string, jobName?: string) => {
    if (!isInTabContext || !tabContext || !projectId) return;
    
    // Check if a tab for this Odyssey job already exists
    const existingTab = tabContext.tabs.find(
      tab => tab.type === 'odyssey' && tab.data?.odysseyJobId === odysseyJobId
    );
    
    if (existingTab) {
      // Tab already exists, just switch to it
      tabContext.switchTab(existingTab.id);
      navigate(`/projects/${projectId}/odyssey/${odysseyJobId}`);
      return;
    }
    
    // Create the tab
    tabContext.openTab({
      type: 'odyssey',
      title: jobName || 'Loading...', // Use provided name if available
      icon: Dna,
      closable: true,
      projectId,
      data: { odysseyJobId },
      loading: !jobName // Only set loading if we don't have a job name
    });
    
    // Navigate after creating the tab
    navigate(`/projects/${projectId}/odyssey/${odysseyJobId}`);
  };
  
  const openMoleculeTab = (moleculeId: string, moleculeName?: string, runName?: string) => {
    if (!isInTabContext || !tabContext || !projectId) return;
    
    console.log('🧪 Opening molecule tab:', { moleculeId, moleculeName, runName, projectId });
    
    // Check if a tab for this molecule already exists
    const existingTab = tabContext.tabs.find(
      tab => tab.type === 'molecule' && tab.data?.moleculeId === moleculeId
    );
    
    if (existingTab) {
      // Tab already exists, just switch to it
      console.log('🔄 Switching to existing molecule tab:', existingTab.id);
      tabContext.switchTab(existingTab.id);
      navigate(`/projects/${projectId}/molecules/${moleculeId}`);
      return;
    }
    
    console.log('🆕 Creating new molecule tab');
    // Create the tab
    tabContext.openTab({
      type: 'molecule',
      title: moleculeName || runName || 'Loading...', // Use provided name if available
      icon: FileCode,
      closable: true,
      projectId,
      data: { 
        moleculeId,
        runName 
      },
      loading: !moleculeName && !runName // Only set loading if we don't have a name
    });
    
    // Navigate after creating the tab
    console.log('🚀 Navigating to molecule URL:', `/projects/${projectId}/molecules/${moleculeId}`);
    navigate(`/projects/${projectId}/molecules/${moleculeId}`);
  };
  
  return {
    isInTabContext,
    projectId,
    openGenerationTab,
    openEntryTab,
    openTableTab,
    openOdysseyTab,
    openMoleculeTab
  };
}